import axios from 'axios';
import { ECOMMERCE_CONFIG, ecommerceError } from '../../config/EcommerceConfig';

/**
 * Servicio para obtener productos públicos del e-commerce
 * Conecta con ProductoPublicController del backend
 */
export const EcommerceProductoService = {
    /**
     * Obtiene todos los productos de una empresa
     * @param {number} idEmpresa - ID de la empresa
     * @param {object} filtros - Filtros opcionales { idCategoria, q (search) }
     * @returns {Promise<Array>} Lista de productos
     */
    obtenerProductos: async (idEmpresa = ECOMMERCE_CONFIG.EMPRESA_ID, filtros = {}) => {
        try {
            const params = new URLSearchParams();
            if (filtros.idCategoria) params.append('idCategoria', filtros.idCategoria);
            if (filtros.q) params.append('q', filtros.q);

            const url = `${ECOMMERCE_CONFIG.API_BASE_URL}/api/productos/public/empresa/${idEmpresa}?${params.toString()}`;
            
            const response = await axios.get(url, {
                timeout: ECOMMERCE_CONFIG.REQUEST_TIMEOUT
            });
            
            // Validar que la respuesta sea correcta
            if (response.data?.success && response.data?.data) {
                return response.data.data;
            }
            ecommerceError('Respuesta inválida del servidor', response.data);
            return [];
        } catch (error) {
            ecommerceError(`Error al obtener productos: ${error.message}`, error);
            throw error;
        }
    },

    /**
     * Obtiene un producto específico con detalles
     * @param {number} idEmpresa - ID de la empresa
     * @param {number} idProducto - ID del producto
     * @returns {Promise<Object>} Datos completos del producto
     */
    obtenerProductoDetalle: async (idEmpresa = ECOMMERCE_CONFIG.EMPRESA_ID, idProducto) => {
        try {
            const url = `${ECOMMERCE_CONFIG.API_BASE_URL}/api/productos/public/empresa/${idEmpresa}/producto/${idProducto}`;
            
            const response = await axios.get(url, {
                timeout: ECOMMERCE_CONFIG.REQUEST_TIMEOUT
            });
            
            if (response.data?.success && response.data?.data) {
                return response.data.data;
            }
            return null;
        } catch (error) {
            ecommerceError(`Error al obtener detalle del producto: ${error.message}`, error);
            throw error;
        }
    },

    /**
     * Realiza una cotización de productos
     * Útil para validar disponibilidad y calcular totales antes de crear pedido
     * @param {number} idEmpresa - ID de la empresa
     * @param {Array} items - Items a cotizar: [{ idVariante, cantidad }, ...]
     * @returns {Promise<Object>} Cotización con totales y detalles
     */
    cotizar: async (idEmpresa = ECOMMERCE_CONFIG.EMPRESA_ID, items) => {
        try {
            const url = `${ECOMMERCE_CONFIG.API_BASE_URL}/api/productos/public/empresa/${idEmpresa}/cotizar`;
            
            const response = await axios.post(url, { items }, {
                timeout: ECOMMERCE_CONFIG.REQUEST_TIMEOUT
            });
            
            if (response.data?.success && response.data?.data) {
                return response.data.data;
            }
            return null;
        } catch (error) {
            ecommerceError(`Error en cotización: ${error.message}`, error);
            throw error;
        }
    },

    /**
     * Busca productos por nombre (necesita idCategoria para mejor precisión)
     * @param {number} idEmpresa - ID de la empresa
     * @param {string} searchTerm - Término de búsqueda
     * @returns {Promise<Array>} Productos encontrados
     */
    buscarProductos: async (idEmpresa = ECOMMERCE_CONFIG.EMPRESA_ID, searchTerm) => {
        try {
            const url = `${ECOMMERCE_CONFIG.API_BASE_URL}/api/productos/public/empresa/${idEmpresa}?q=${encodeURIComponent(searchTerm)}`;
            
            const response = await axios.get(url, {
                timeout: ECOMMERCE_CONFIG.REQUEST_TIMEOUT
            });
            
            if (response.data?.success && response.data?.data) {
                return response.data.data;
            }
            return [];
        } catch (error) {
            ecommerceError(`Error en búsqueda de productos: ${error.message}`, error);
            throw error;
        }
    }
};
